import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useServices } from '../../app/services';
import { currencyExponent } from '../../domain/money';
import { spendingRule, type RecurringKind, type RecurringTemplate } from '../../domain/model';
import { parseAmount } from '../../i18n/parseAmount';
import { PERIODS_PER_YEAR } from '../../domain/annualize';
import { BottomSheet, Button, ChoiceList, SegmentedControl, SelectField, TextField, useFormat, useToast } from '../../ui';
import { loadAllPicks, type QuickPick } from '../quick-add/quickCategories';
import type { BillsData } from './bills';
import { PersonField, useMemberName } from '../entries/people';

const keyOf = (categoryId: string, subcategoryId: string | null) => `${categoryId}/${subcategoryId ?? ''}`;

/**
 * Add or edit a recurring expense: what it is (category, name), whether the
 * amount is the same every month, and optionally the due day. Paused and
 * deleted from here too.
 */
export function TemplateSheet({
  open,
  template,
  data,
  onClose,
}: {
  open: boolean;
  template: RecurringTemplate | null;
  data: BillsData;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { store, activeSpace, notifyDataChanged } = useServices();
  const { formatLocale, money } = useFormat();
  const toast = useToast();
  const memberName = useMemberName();
  const [groups, setGroups] = useState<{ category: QuickPick; subs: QuickPick[] }[]>([]);
  const [pickKey, setPickKey] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<RecurringKind>('fixed');
  const [amount, setAmount] = useState('');
  const [day, setDay] = useState('');
  const [paidBy, setPaidBy] = useState('');
  const [cardCounts, setCardCounts] = useState<'counts' | 'tracked' | null>(null);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const spaceId = activeSpace?.id;
  const currency = template?.currency ?? activeSpace?.currency ?? 'USD';

  useEffect(() => {
    if (!open || !spaceId) return;
    let live = true;
    void loadAllPicks(store, spaceId).then((g) => {
      if (!live) return;
      setGroups(g);
      if (!template) setPickKey((k) => k || (g[0] ? keyOf(g[0].category.categoryId, null) : ''));
    });
    return () => {
      live = false;
    };
  }, [open, store, spaceId, template]);

  // Reset the form whenever the sheet opens.
  useEffect(() => {
    if (!open) return;
    setPickKey(template ? keyOf(template.categoryId, template.subcategoryId) : '');
    setName(template?.name ?? '');
    setKind(template?.kind ?? 'fixed');
    setAmount(
      template?.amountMinor
        ? (template.amountMinor / 10 ** currencyExponent(template.currency)).toFixed(currencyExponent(template.currency))
        : '',
    );
    setDay(template?.dayOfMonth ? String(template.dayOfMonth) : '');
    setPaidBy(template?.paidBy ?? '');
    setCardCounts(template && spendingRule(template.type) === 'choose' ? (template.countsAsSpending ? 'counts' : 'tracked') : null);
    setTouched(false);
    setFailed(false);
  }, [open, template]);

  const picks = groups.flatMap((g) => [g.category, ...g.subs]);
  const picked = picks.find((p) => keyOf(p.categoryId, p.subcategoryId) === pickKey) ?? null;
  const labelOf = (p: QuickPick) => p.name ?? t(p.labelKey ?? 'categories.other');
  const isCard = picked ? picked.type === 'credit_card_payment' : template?.type === 'credit_card_payment';
  const parsed = amount.trim() ? parseAmount(amount, currency) : null;
  const amountError =
    kind !== 'fixed'
      ? undefined
      : parsed === 'invalid'
        ? t('quickAdd.invalid')
        : touched && (parsed === null || parsed === 0)
          ? t('recurring.amountRequired')
          : undefined;

  async function save() {
    if (!spaceId || busy) return;
    setTouched(true);
    if (kind === 'fixed' && (typeof parsed !== 'number' || parsed <= 0)) return;
    if (isCard && !cardCounts) return;
    const categoryId = picked?.categoryId ?? template?.categoryId;
    if (!categoryId) return;
    const subcategoryId = picked ? picked.subcategoryId : (template?.subcategoryId ?? null);
    const type = picked?.type ?? template!.type;
    const fields = {
      categoryId,
      subcategoryId,
      type,
      name: name.trim() || (picked ? labelOf(picked) : template!.name),
      kind,
      amountMinor: kind === 'fixed' ? (parsed as number) : null,
      dayOfMonth: day ? Number(day) : null,
      paidBy: paidBy || null,
    };
    setBusy(true);
    setFailed(false);
    try {
      if (template) {
        const rule = spendingRule(type);
        await store.recurring.update(template.id, {
          ...fields,
          countsAsSpending: rule === 'choose' ? cardCounts === 'counts' : rule,
        });
      } else {
        await store.recurring.createFor({
          spaceId,
          ...fields,
          countsAsSpending: isCard ? cardCounts === 'counts' : undefined,
        });
      }
      notifyDataChanged();
      onClose();
    } catch (e) {
      console.error('[recurring]', e);
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  async function togglePause() {
    if (!template) return;
    await store.recurring.update(template.id, { active: !template.active });
    notifyDataChanged();
    onClose();
    if (template.active)
      toast.show({
        message: t('recurring.pausedToast', { name: template.name }),
      });
  }

  async function remove() {
    if (!template) return;
    await store.recurring.remove(template.id);
    notifyDataChanged();
    onClose();
    toast.show({
      message: t('recurring.deleted', { name: template.name }),
      action: {
        label: t('quickAdd.undo'),
        onClick: () => void store.recurring.restore(template.id).then(notifyDataChanged),
      },
    });
  }

  const currentMissing = template && !picked && groups.length > 0;

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={template ? t('recurring.edit') : t('recurring.new')}
      footer={
        <>
          <Button variant="primary" size="lg" block loading={busy} onClick={() => void save()}>
            {template ? t('recurring.save') : t('recurring.create')}
          </Button>
          {template && (
            <div className="bill-sheet-actions">
              <Button variant="ghost" onClick={() => void togglePause()}>
                {template.active ? t('recurring.pause') : t('recurring.resume')}
              </Button>
              <Button variant="danger" onClick={() => void remove()}>
                {t('recurring.delete')}
              </Button>
            </div>
          )}
        </>
      }
    >
      <div className="stack">
        <SelectField label={t('recurring.category')} value={pickKey} onChange={(e) => setPickKey(e.target.value)}>
          {currentMissing && <option value={pickKey}>{template.name}</option>}
          {groups.map((g) => (
            <optgroup key={g.category.id} label={labelOf(g.category)}>
              <option value={keyOf(g.category.categoryId, null)}>{labelOf(g.category)}</option>
              {g.subs.map((s) => (
                <option key={s.id} value={keyOf(s.categoryId, s.subcategoryId)}>
                  {labelOf(s)}
                </option>
              ))}
            </optgroup>
          ))}
        </SelectField>
        <TextField
          label={t('recurring.name')}
          hint={t('recurring.nameHint')}
          value={name}
          maxLength={60}
          placeholder={picked ? labelOf(picked) : undefined}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="stack stack-xs">
          <SegmentedControl<RecurringKind>
            label={t('recurring.kind')}
            value={kind}
            onChange={setKind}
            options={[
              { value: 'fixed', label: t('recurring.kindFixed') },
              { value: 'variable', label: t('recurring.kindVariable') },
            ]}
          />
          <p className="field-msg">{kind === 'fixed' ? t('recurring.kindFixedBody') : t('recurring.kindVariableBody')}</p>
        </div>
        {kind === 'fixed' && (
          <TextField
            label={t('recurring.amount')}
            inputMode="decimal"
            autoComplete="off"
            value={amount}
            placeholder={(0).toLocaleString(formatLocale, {
              minimumFractionDigits: currencyExponent(currency),
            })}
            onChange={(e) => setAmount(e.target.value)}
            error={amountError}
            // What it costs over a year (V1.5): a small monthly amount is rarely small over twelve.
            hint={
              typeof parsed === 'number' && parsed > 0
                ? t('annual.hint', { amount: money(parsed * PERIODS_PER_YEAR[template?.frequency ?? 'monthly'], { currency }) })
                : undefined
            }
          />
        )}
        <SelectField label={t('recurring.dueDay')} hint={t('recurring.dueDayHint')} value={day} onChange={(e) => setDay(e.target.value)}>
          <option value="">{t('recurring.dueDayNone')}</option>
          {Array.from({ length: 31 }, (_, i) => (
            <option key={i + 1} value={String(i + 1)}>
              {i + 1}
            </option>
          ))}
        </SelectField>
        {data.members.length > 1 && (
          <PersonField
            label={t('quickAdd.paidBy')}
            value={paidBy}
            onChange={setPaidBy}
            options={[{ value: '', label: t('recurring.whoRecords') }, ...data.members.map((m) => ({ value: m.id, label: memberName(m) }))]}
          />
        )}
        {isCard && (
          <div className="stack stack-xs">
            <p className="field-label">{t('quickAdd.card.label')}</p>
            <ChoiceList<'counts' | 'tracked'>
              label={t('quickAdd.card.label')}
              value={cardCounts}
              onChange={setCardCounts}
              options={[
                {
                  value: 'counts',
                  label: t('quickAdd.card.counts'),
                  description: t('quickAdd.card.countsBody'),
                },
                {
                  value: 'tracked',
                  label: t('quickAdd.card.tracked'),
                  description: t('quickAdd.card.trackedBody'),
                },
              ]}
            />
            {touched && !cardCounts && (
              <p className="field-msg field-error" role="alert">
                {t('quickAdd.card.required')}
              </p>
            )}
          </div>
        )}
        {failed && (
          <p className="field-msg field-error" role="alert">
            {t('recurring.error')}
          </p>
        )}
      </div>
    </BottomSheet>
  );
}
